import { Injectable } from '@angular/core';
import { RestService } from '../../services/common';

@Injectable({
  providedIn: 'root'
})
export class ExplorationService {
  public showaddUpdateExplorationModal: boolean = false;

  constructor(private restService: RestService) { }

  /**
   * Get all projects under an exploration from local storage userProjects list
   * @param explorationId The exploration ID
   * @returns Array of filtered projects
   */
  getUserProjectListByExplorationId(explorationId: any): any[] {
    try {
      let userProjectListStr = localStorage.getItem("userProjects");
      if (userProjectListStr) {
        const userProjectList = JSON.parse(userProjectListStr) || [];
        return userProjectList.filter((x: any) =>
          (x.explorationId ?? x.ExplorationId ?? x.explorationid ?? x.exploration?.id) == explorationId
        );
      }
    } catch (e) {
      console.error('Error fetching projects from local storage', e);
    }
    return [];
  }

  /**
   * Patch exploration data
   * @param data The exploration data to patch
   * @param id The exploration ID
   * @returns Observable with response
   */
  patchExploration(data: any, id: any) {
    return this.restService.patchData('exploration/' + id, null, data);
  }

  /**
   * Filter and retrieve explorations
   * @param siteId (optional) site Id to filter explorations for a specific site
   * @param userId (optional) user Id to filter explorations assigned to a user
   * @param explorationId (optional) specific exploration Id to fetch
   * @param isActive (optional) filter by active status (default: true)
   * @returns Observable with filtered explorations
   */
  filterExplorations(siteId?: any, userId?: any, explorationId?: any, isActive: boolean = true) {
    let localUrl: any = 'exploration?isactive=' + isActive;

    // Check if any filter is applied and set filter params
    localUrl = siteId ? localUrl + '&siteid=' + siteId : localUrl;
    localUrl = userId ? localUrl + '&userid=' + userId : localUrl;
    localUrl = explorationId ? localUrl + '&explorationid=' + explorationId : localUrl;

    return this.restService.getData(localUrl);
  }

  /**
   * Get all explorations
   * @param isActive (optional) filter by active status (default: true)
   * @returns Observable with list of all explorations
   */
  getExplorations(isActive: boolean = true) {
    return this.restService.getData('exploration?isactive=' + isActive);
  }

  /**
   * Get exploration by ID
   * @param id The exploration ID
   * @returns Observable with exploration details
   */
  getExplorationById(id: number) {
    return this.restService.getData('exploration/' + id);
  }

  /**
   * Create a new exploration
   * @param data The exploration data (name, description, siteId, etc.)
   * @returns Observable with response
   */
  createExploration(data: any) {
    return this.restService.postData('exploration', data);
  }

  /**
   * Update entire exploration
   * @param id The exploration ID
   * @param data The exploration data to update
   * @returns Observable with response
   */
  updateExploration(id: number, data: any) {
    return this.restService.updateData('exploration/' + id, null, data);
  }

  /**
   * Partially update exploration (name, status, description, siteId)
   * @param id The exploration ID
   * @param data The partial exploration data
   * @returns Observable with response
   */
  updateExplorationPartial(id: number, data: any) {
    return this.restService.patchData('exploration/' + id, null, data);
  }

  /**
   * Update exploration location link only
   * @param id The exploration ID
   * @param locationLink The location link to update
   * @returns Observable with response
   */
  updateExplorationLocation(id: number, locationLink: string) {
    const requestPayload = {
      locationLink: locationLink
    };
    return this.restService.patchData('exploration/' + id + '/update-location', null, requestPayload);
  }

  /**
   * Filter user explorations
   * @param userId (optional) user ID to filter explorations assigned to the user
   * @param explorationId (optional) specific exploration ID to filter
   * @param isActive (optional) filter by active status (default: true)
   * @returns Observable with filtered user explorations
   */
  filterUserExplorations(userId?: any, explorationId?: any, isActive: boolean = true) {
    let localUrl: any = 'user-explorations?isactive=' + isActive;

    // Check if any filter is applied and set filter params
    localUrl = userId ? localUrl + '&userid=' + userId : localUrl;
    localUrl = explorationId ? localUrl + '&explorationid=' + explorationId : localUrl;

    return this.restService.getData(localUrl);
  }

  /**
   * Bulk assign multiple users to an exploration
   * Creates separate UserExploration records for each user ID
   * @param explorationId The exploration ID to assign users to
   * @param userIds Array of user IDs to assign
   * @returns Observable with response from server
   */
  bulkAssignUsersToExploration(explorationId: number, userIds: number[]) {
    const requestPayload = {
      explorationId: explorationId,
      userIds: userIds
    };
    return this.restService.postData('user-explorations/bulk-user-assign', requestPayload);
  }

  /**
   * Update/Replace users assigned to an exploration
   * Removes all existing assignments and creates new ones with provided user IDs
   * @param explorationId The exploration ID to update
   * @param userIds Array of user IDs to assign (replaces existing)
   * @returns Observable with response from server
   */
  updateExplorationUsers(explorationId: number, userIds: number[]) {
    const requestPayload = {
      explorationId: explorationId,
      userIds: userIds
    };
    // Use PUT method to match backend endpoint
    return this.restService.updateData('user-explorations/bulk-user-replace', null, requestPayload);
  }

  /**
   * Remove a user from an exploration
   * @param explorationId The exploration ID
   * @param userId The user ID to remove
   * @returns Observable with response
   */
  removeUserFromExploration(explorationId: number, userId: number) {
    return this.restService.delete(`user-explorations/${explorationId}/users/${userId}`);
  }

  /**
   * Update a user's role in an exploration
   * @param explorationId The exploration ID
   * @param userId The user ID
   * @param role The new role
   * @returns Observable with response
   */
  updateUserRole(explorationId: number, userId: number, role: string) {
    return this.restService.updateData(`user-explorations/${explorationId}/users/${userId}/role`, null, { role });
  }

  /**
   * Get all users assigned to an exploration
   * @param explorationId The exploration ID
   * @returns Observable with list of assigned users
   */
  getExplorationUsers(explorationId: number) {
    return this.restService.getData(`user-explorations/${explorationId}/users`);
  }

  /**
   * Delete exploration (soft delete by marking as inactive)
   * @param id The exploration ID to delete
   * @returns Observable with response
   */
  deleteExploration(id: number) {
    return this.restService.deleteData(`exploration`, id);
  }

  //#region LOCAL STORAGE

  getUserExplorationList(): any[] | null {
    const raw = localStorage.getItem('userExplorations');
    if (raw) {
      const list = JSON.parse(raw);
      return list ?? null;
    }
    return null;
  }

  getUserExplorationListBySiteId(siteId: any): any[] | null {
    const list = this.getUserExplorationList();
    if (list) {
      const filtered = list.filter((x: any) => (x.siteId ?? x.SiteId) == siteId);
      return filtered.length > 0 ? filtered : null;
    }
    return null;
  }

  /**
   * Fetch user explorations from API using the exploration endpoint
   * This returns full exploration objects with all fields (siteId, status, description, etc.)
   * @param userId The user ID
   */
  getUserExplorationsFromAPI(userId: any): void {
    if (!userId) return;
    // Use filterExplorations endpoint which returns full exploration objects
    this.filterExplorations(undefined, userId, undefined, true)
      .subscribe((data: any) => {
        if (data?.result) {
          localStorage.setItem('userExplorations', JSON.stringify(data.result));
          if (data.result.length > 0) {
            localStorage.setItem('selectedExploration', JSON.stringify(data.result[0]));
          }
        }
      });
  }

  //#endregion
}
